import { Component, inject } from '@angular/core';
import { DataOptions, DataResponse, DataService } from '../../services/data.service';
import { AttrPath, Table } from '@cumlaude/metadata';
import { Observable } from 'rxjs';
import { maxOver, sumOver } from '../../services/aggregation';
import { getLeafA, Path } from '../../services/data-tree';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { Attributes } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { AlternatingMode, ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { createMeasureColumn, DataRow, DataTreeTableComponent, getAlternatingRowgroup } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { DataTreeTableConfig } from '../../shared/dashboard/data-tree-table/data-tree-table-config';
import { att } from '../../services/measures';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';

interface CijferkolommenDetailsI extends Attributes {
	ck_m_is_any_laatste_gemiddelde: number;
	ck_m_count_cijfers_laatste_gemiddelde: number;
}

interface CijferkolommenDetailsA extends Attributes {
	ck_m_is_any_laatste_gemiddelde: number;
	ck_m_count_cijfers_laatste_gemiddelde: number;
}

@Component({
	selector: 'app-cijferkolommen-details',
	templateUrl: './cijferkolommen-details.component.html',
	styleUrls: ['./cijferkolommen-details.component.scss'],
	imports: [DashboardContainerComponent, FilterPanelComponent, DashboardHeaderComponent, DataTreeTableComponent, FilterBarComponent],
})
export class CijferkolommenDetailsComponent extends DataTreeTableConfig<CijferkolommenDetailsI, CijferkolommenDetailsA> {
	private readonly dataService = inject(DataService);

	selectedGroups: AttrPath[] = [['ck_nm_opleiding'], ['ck_nm_vak'], ['ck_nm_periode'], ['ck_co_kolom'], ['ck_abb_kolomkop']];

	override fixedBeforeGroups = 5;

	defaultFilters: FilterName[] = ['ck_nm_schooljaar', 'ck_nm_vestiging', 'ck_abb_kolomkop', 'ck_nm_opleiding', 'ck_nm_vak'];

	permanentFilterExpressions = [];

	factTable = Table.fac_ck_cijferkolommen;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		const { f, having } = this.dataService.moveToHaving([['ck_abb_kolomkop']], options);
		return this.dataService.getCijferkolommenData({ ...options, f, having });
	}

	protected override singleAggregators = {
		ck_m_is_any_laatste_gemiddelde: maxOver('ck_m_is_any_laatste_gemiddelde'),
		ck_m_count_cijfers_laatste_gemiddelde: sumOver<'ck_m_count_cijfers_laatste_gemiddelde', CijferkolommenDetailsI, number>(
			'ck_m_count_cijfers_laatste_gemiddelde'
		),
	};

	override createMeasureColumns(
		_context: DashboardContext<
			CijferkolommenDetailsI,
			CijferkolommenDetailsA,
			DataTreeTableConfig<CijferkolommenDetailsI, CijferkolommenDetailsA>
		>
	): ColumnDef<DataRow<CijferkolommenDetailsA>>[] {
		return [
			createMeasureColumn('Aantal keer gebruikt', (path) => {
				const aantal = att<'ck_m_count_cijfers_laatste_gemiddelde', CijferkolommenDetailsA>('ck_m_count_cijfers_laatste_gemiddelde')(path);
				return aantal > 0 ? aantal : null;
			}),
		];
	}

	override enrichTableModel(
		_context: DashboardContext<
			CijferkolommenDetailsI,
			CijferkolommenDetailsA,
			DataTreeTableConfig<CijferkolommenDetailsI, CijferkolommenDetailsA>
		>,
		tableModel: TableModel<DataRow<CijferkolommenDetailsA>>
	) {
		tableModel.showFooters = false;
		tableModel.alternating = AlternatingMode.LINE;
		tableModel.getRowgroup = (rowModel: DataRow<CijferkolommenDetailsA>) => ({
			class: this.getRowClass(rowModel._path),
			...getAlternatingRowgroup(rowModel, 4),
		});
	}

	getRowClass(path: Path<CijferkolommenDetailsA, number[]>): string {
		const { ck_m_is_any_laatste_gemiddelde } = getLeafA(path);
		return ck_m_is_any_laatste_gemiddelde == 1 ? 'hilite' : '';
	}
}
